import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_API || "";

export function GlobalSearch({ setTab }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const headers = () => {
    const t = localStorage.getItem("token") || sessionStorage.getItem("token");
    return { Authorization: `Bearer ${t}` };
  };

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/api/crossstudio/search`, { headers: headers(), params: { q: query } });
        setResults(r.data.results || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  const navigate = (item) => {
    if (item.link && setTab) setTab(item.link);
    setOpen(false);
    setQuery("");
    setResults([]);
  };

  const studioColors = {
    "CRM": "bg-blue-100 text-blue-700",
    "Angebote & Rechnungen": "bg-green-100 text-green-700",
    "Project & Workforce": "bg-purple-100 text-purple-700",
    "Expense & Document": "bg-orange-100 text-orange-700",
    "Commerce": "bg-pink-100 text-pink-700",
    "Business Travel": "bg-cyan-100 text-cyan-700",
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 100); }}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-sm text-gray-500 transition-colors"
      >
        <Search className="w-4 h-4" />
        <span className="hidden md:inline">Suchen...</span>
        <kbd className="hidden md:inline text-[10px] bg-gray-200 px-1.5 py-0.5 rounded">Ctrl+K</kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/20">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl border overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Kunden, Rechnungen, Projekte, Produkte suchen..."
                className="flex-1 outline-none text-sm"
                autoFocus
              />
              <button onClick={() => { setOpen(false); setQuery(""); setResults([]); }}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {loading && <div className="p-4 text-sm text-gray-400">Suche...</div>}
              {!loading && query.length >= 2 && results.length === 0 && (
                <div className="p-4 text-sm text-gray-400">Keine Ergebnisse für "{query}"</div>
              )}
              {results.map((r, i) => (
                <button
                  key={i}
                  onClick={() => navigate(r)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left border-b last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.titel}</div>
                    <div className="text-xs text-gray-500 truncate">{r.untertitel}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${studioColors[r.studio] || "bg-gray-100 text-gray-600"}`}>{r.studio}</span>
                    <span className="text-[10px] text-gray-400">{r.typ}</span>
                  </div>
                </button>
              ))}
            </div>
            {query.length < 2 && !loading && (
              <div className="p-4 text-xs text-gray-400 text-center">Mindestens 2 Zeichen eingeben</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
